import React, { useState } from 'react';
import { STATUS_COLORS, STATUS_LABELS, STATUS_PRIORITY } from '../../utils/constants';

const StatusPieChart = ({ statuses, size = 24, colors = STATUS_COLORS, labels = STATUS_LABELS }) => {
  const [hovered, setHovered] = useState(false);

  if (!statuses) return null;

  const entries = Object.entries(statuses)
    .sort((a, b) => {
      const ai = STATUS_PRIORITY.indexOf(a[0]);
      const bi = STATUS_PRIORITY.indexOf(b[0]);
      return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
    });
  const total = entries.reduce((s, [, c]) => s + c, 0);
  if (total === 0) return null;

  const hoverSize = size * 1.5;
  const currentSize = hovered ? hoverSize : size;
  const explode = hovered && entries.length > 1 ? 2 : 0;

  const r = currentSize / 2 - 2 - explode;
  const cx = currentSize / 2;
  const cy = currentSize / 2;
  let cumulative = 0;

  const slices = entries.map(([status, count]) => {
    const frac = count / total;
    const startAngle = cumulative * 2 * Math.PI - Math.PI / 2;
    cumulative += frac;
    const endAngle = cumulative * 2 * Math.PI - Math.PI / 2;
    const fill = colors[status] || '#616161';

    // Explode offset: push slice outward from its midpoint angle
    const midAngle = (startAngle + endAngle) / 2;
    const dx = explode * Math.cos(midAngle);
    const dy = explode * Math.sin(midAngle);

    if (entries.length === 1) {
      return <circle key={status} cx={cx + dx} cy={cy + dy} r={r} fill={fill} />;
    }

    const largeArc = frac > 0.5 ? 1 : 0;
    const x1 = cx + dx + r * Math.cos(startAngle);
    const y1 = cy + dy + r * Math.sin(startAngle);
    const x2 = cx + dx + r * Math.cos(endAngle);
    const y2 = cy + dy + r * Math.sin(endAngle);

    return (
      <path
        key={status}
        d={`M${cx + dx},${cy + dy} L${x1},${y1} A${r},${r} 0 ${largeArc} 1 ${x2},${y2} Z`}
        fill={fill}
      />
    );
  });

  return (
    <div
      style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <svg
        width={currentSize}
        height={currentSize}
        viewBox={`0 0 ${currentSize} ${currentSize}`}
        className="flex-shrink-0"
        style={{
          display: 'inline-block',
          transition: 'width 0.2s ease, height 0.2s ease',
        }}
      >
        {slices}
      </svg>

      {/* Legend on hover */}
      {hovered && (
        <div
          style={{
            position: 'absolute',
            left: currentSize + 8,
            top: '50%',
            transform: 'translateY(-50%)',
            background: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: 8,
            padding: '6px 10px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            whiteSpace: 'nowrap',
            zIndex: 50,
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
          }}
        >
          {entries.map(([status, count]) => (
            <div key={status} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: colors[status] || '#616161',
                  flexShrink: 0,
                }}
              />
              <span style={{ fontSize: '0.72rem', color: '#222', fontWeight: 500 }}>
                {labels[status] || status}
              </span>
              <span style={{ fontSize: '0.72rem', color: '#7C7B7B', fontWeight: 600 }}>
                {count}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default StatusPieChart;
